import React, { createContext, useState, useEffect, useContext, useCallback, useMemo } from 'react';
import { collection, query, orderBy, collectionGroup, doc, writeBatch, updateDoc, serverTimestamp, where, onSnapshot, addDoc, limit, getDocs } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../firebase.js';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import app from '../firebase.js';
import { useAuth } from '../context/AuthContext.js'; // Import useAuth
import { ALL_PERMISSIONS } from '../constants/permissions.js';
import { ROLE_PERMISSION_PRESETS } from '../constants/permissionCatalog.js';

// --- OPTIMIZACIÓN DE RENDIMIENTO ---
// Los acuerdos se cargan COMPLETOS porque son críticos para:
// - Pagos globales (necesita ver TODOS los acuerdos activos)
// - Gestión de inversiones (aprobar/rechazar todos)
// - Panel financiero (resumen global)
//
// Los perfiles de usuario se cargan BAJO DEMANDA porque:
// - El admin busca usuarios específicos cuando necesita editarlos
// - No es necesario tener todos los perfiles en memoria al inicio
//
// Datos históricos (retiros, financial log) tienen PAGINACIÓN porque:
// - Son datos históricos que crecen indefinidamente
// - El admin puede cargar más cuando necesite ver datos antiguos

const MySwal = withReactContent(Swal);
const functions = getFunctions(app);

// Helper to create grouped callable functions
const createApiCallable = (functionName) => {
    const callable = httpsCallable(functions, 'api');
    return (data) => {
        const requestData = { ...data, __fn: functionName };
        return callable(requestData);
    };
};

const normalizeRolePreset = (preset = {}) => {
    const normalized = {};
    ALL_PERMISSIONS.forEach(permission => {
        normalized[permission] = !!preset[permission];
    });
    return normalized;
};

const deriveRolePresets = (config) => {
    const stored = config?.rolePermissionPresets || {};
    return {
        admin: normalizeRolePreset({ ...(ROLE_PERMISSION_PRESETS.admin || {}), ...(stored.admin || {}) }),
        support: normalizeRolePreset({ ...(ROLE_PERMISSION_PRESETS.support || {}), ...(stored.support || {}) }),
        user: normalizeRolePreset({ ...(ROLE_PERMISSION_PRESETS.user || {}), ...(stored.user || {}) }),
    };
};

// --- Callable Cloud Functions ---
const deleteUserCallable = createApiCallable('deleteUserAndData');
const reactivateUserCallable = createApiCallable('reactivateUser');
const processWithdrawalCallable = createApiCallable('processWithdrawal');
const adminAdjustUserBalanceCallable = createApiCallable('adminAdjustUserBalance');
const processSmallOwnerRequestCallable = createApiCallable('processSmallOwnerRequest');
// const migrateOldAgreementsCallable = createApiCallable('migrateExistingAgreements'); // Obsoleto
const bootstrapAdminCallable = createApiCallable('bootstrapAdmin');
const reprocessStuckAgreementsCallable = createApiCallable('reprocessStuckAgreements');
const assignDailyGainsCallable = createApiCallable('assignDailyGains');
const revertLastPaymentCallable = createApiCallable('revertLastPayment');
const assignGainsByDateCallable = createApiCallable('assignGainsByDate'); // <-- NUEVO
const cleanReversalHistoryCallable = createApiCallable('cleanReversalHistory'); // <-- NUEVO
const resyncAllBalancesCallable = createApiCallable('resyncAllBalances');
const revertGainsByDateCallable = createApiCallable('revertGainsByDate');
const recalculateMissingCommissionsCallable = createApiCallable('recalculateMissingCommissions');
const correctNegativeBalancesCallable = createApiCallable('correctNegativeBalances');
// const recalculateAllCommissionsCallable = createApiCallable('recalculateAllCommissions'); // Redundante
// const deleteAllCommissionsCallable = createApiCallable('deleteAllCommissions'); // Peligroso
const cleanAndRebuildAllCommissionsCallable = createApiCallable('cleanAndRebuildAllCommissions');
const verifyAndPayMissingCommissionsCallable = createApiCallable('verifyAndPayMissingCommissions');
const adminCreateManualAgreementCallable = createApiCallable('adminCreateManualAgreement');
const resyncTeamSizesCallable = createApiCallable('resyncTeamSizes');
const recalculateAllAgreementsProgressCallable = createApiCallable('recalculateAllAgreementsProgress');
const correctInvestmentTransactionsCallable = createApiCallable('correctInvestmentTransactions');
const cancelAgreementCallable = createApiCallable('cancelAgreement');
const deleteAllCommissionsCallable = createApiCallable('deleteAllCommissions');
const auditAndCorrectAgreementsCallable = createApiCallable('auditAndCorrectAgreements');
const deactivateAcceleratorCallable = createApiCallable('deactivateAccelerator'); // <-- AÑADIDO
const manualReactivateAcceleratorCallable = createApiCallable('manualReactivateAccelerator');
const approveAcceleratorRequestCallable = createApiCallable('approveAcceleratorRequest');
const fixAgreementIntegrityCallable = createApiCallable('fixAgreementIntegrity');
const togglePlatformSettingCallable = createApiCallable('togglePlatformSetting'); // <-- NUEVO: Callable para la función de backend
const recalculateAllRanksCallable = createApiCallable('recalculateAllRanks');
const recalculateAllCommissionsCallable = createApiCallable('recalculateAllCommissions');
const getFinancialLogCallable = createApiCallable('getFinancialLog'); // <-- NUEVO: Para paginación del servidor

export const AdminContext = createContext();

export const useAdminContext = () => useContext(AdminContext);

export const AdminProvider = ({ children }) => {
    const { currentUser, userProfile, isAdmin, isSupport, platformConfig } = useAuth(); // Get currentUser and isAdmin from AuthContext
    const [users, setUsers] = useState([]); // Inicializar como array vacío
    // Los retiros ya NO se cargan acá: antes había un listener global capado a las 50 solicitudes
    // más recientes de todo el sitio, así que las viejas (sobre todo pendientes sin resolver)
    // dejaban de verse cuando se acumulaban 50 solicitudes más nuevas. GestionDeRetiros.js ahora
    // pagina directo contra Firestore (página siguiente/anterior reales), por tab de estado.
    const [smallOwnerRequests, setSmallOwnerRequests] = useState([]); // Inicializar como array vacío
    const [financialLog, setFinancialLog] = useState([]); // <-- AÑADIDO: Estado para el registro financiero
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [allAgreements, setAllAgreements] = useState([]); // Inicializar como array vacío
    const [agreementsSummary, setAgreementsSummary] = useState(null);
    const [processingId, setProcessingId] = useState(null);
    const [adminUnreadCount, setAdminUnreadCount] = useState(0);

    const rolePermissionPresets = useMemo(() => deriveRolePresets(platformConfig), [platformConfig]);

    const INITIAL_AGREEMENTS_LOAD = null; // cargar todo (sin límite) para mostrar totales completos

    const hasPrivilegedAccess = isAdmin || isSupport;

    // Carga de datos en tiempo real para el administrador
    useEffect(() => {
        // Si el usuario no es admin, limpiamos los datos y nos detenemos.
        let unsubscribes = [];

        if (!hasPrivilegedAccess) {
            setUsers([]); setFinancialLog([]); setSmallOwnerRequests([]); setAllAgreements([]); setError(null); // Limpiar settings también
            setLoading(false);
            return;
        }

        const setupListeners = () => {
            setLoading(true);

            // --- LÓGICA DE CARGA REESTRUCTURADA Y OPTIMIZADA ---

            // 1. Cargar los datos más importantes primero (acuerdos) y desactivar el loading principal.
            const agreementsQuery = INITIAL_AGREEMENTS_LOAD
                ? query(collection(db, 'public_agreements'), orderBy('createdAt', 'desc'), limit(INITIAL_AGREEMENTS_LOAD))
                : query(collection(db, 'public_agreements'), orderBy('createdAt', 'desc'));
            const unsubAgreements = onSnapshot(agreementsQuery, (snapshot) => {
                const data = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
                setAllAgreements(data);
                setLoading(false); // <-- Desbloquea la UI aquí.
            }, (error) => {
                console.error("Error crítico al cargar acuerdos:", error);
                setError("No se pudieron cargar los acuerdos.");
                setLoading(false);
            });
            unsubscribes.push(unsubAgreements);

            // 1b. Cargar perfiles de usuario usando collectionGroup (para KPI de usuarios y datos completos)
            const usersQuery = query(collectionGroup(db, 'profile'));
            const unsubUsers = onSnapshot(usersQuery, (snapshot) => {
                // El ID del documento es "data", el ID del usuario es el abuelo: users/{userId}/profile/data
                const data = snapshot.docs.map(d => ({ id: d.ref.parent.parent.id, ...d.data() }));
                setUsers(data);
            }, (error) => {
                console.error("Error al cargar perfiles de usuario:", error);
            });
            unsubscribes.push(unsubUsers);

            // 2. Cargar datos históricos con PAGINACIÓN (no críticos para pagos globales)
            const otherQueriesConfig = [
                // REMOVIDO: collectionGroup 'profile' - se carga bajo demanda
                // REMOVIDO: withdraw_requests - ver comentario en la declaración de useState de más arriba
                { q: query(collection(db, 'financial_log'), orderBy('createdAt', 'desc'), limit(100)), setter: setFinancialLog },
                { q: query(collection(db, 'small_owners_requests'), orderBy('createdAt', 'desc'), limit(25)), setter: setSmallOwnerRequests },
            ];

            // Retrasar listeners secundarios para dar prioridad al render inicial.
            setTimeout(() => {
                otherQueriesConfig.forEach(config => {
                    try {
                        const unsub = onSnapshot(config.q, (snapshot) => {
                            const data = snapshot.docs.map(d => ({
                                id: config.isGroup ? d.ref.parent.parent.id : d.id,
                                ...d.data()
                            }));
                            config.setter(data);
                        }, (err) => {
                            console.warn(`Error en listener secundario:`, err);
                        });
                        unsubscribes.push(unsub);
                    } catch (err) {
                        console.warn('Fallo al iniciar listener secundario:', err);
                    }
                });
            }, 300);

            // Obtener resumen exacto de acuerdos desde el backend (callable)
            try {
                const getAgreementsSummaryCallable = createApiCallable('getAgreementsSummary');
                getAgreementsSummaryCallable().then(res => {
                    if (res && res.data) {
                        setAgreementsSummary && setAgreementsSummary(res.data);
                    }
                }).catch(err => {
                    console.warn('No se pudo obtener resumen de acuerdos:', err.message || err);
                });
            } catch (err) {
                console.warn('Error llamando a getAgreementsSummary:', err.message || err);
            }
        };
        
        setupListeners();

        return () => {
            unsubscribes.forEach(unsub => unsub());
        };
    }, [hasPrivilegedAccess, currentUser]); // Añadir currentUser como dependencia para que se re-ejecute si cambia el usuario

    useEffect(() => {
        if (!hasPrivilegedAccess) {
            setAdminUnreadCount(0);
            return;
        }

        const unreadQuery = query(collection(db, 'admin_notifications'), where('read', '==', false));
        const unsubscribe = onSnapshot(unreadQuery, (snapshot) => {
            setAdminUnreadCount(snapshot.size);
        }, (error) => {
            console.error('Error al escuchar notificaciones del admin:', error);
        });

        return () => unsubscribe();
    }, [hasPrivilegedAccess]);

    // Carga de acuerdos con filtros y paginación (Este comentario parece fuera de lugar aquí)
    // --- Generic utility action handler ---
    const createUtilityAction = useCallback((callable, processingKey, swalOptions) => {
        return async (data = {}) => {
            const confirmResult = await MySwal.fire(swalOptions);
    
            if (!confirmResult.isConfirmed) return;
    
            setProcessingId(processingKey);
            try {
                const result = await callable(data);
                MySwal.fire('¡Éxito!', result.data.message, 'success');
            } catch (err) {
                console.error(`Error en la utilidad '${processingKey}':`, err);
                MySwal.fire('Error', err.message || `No se pudo ejecutar la acción '${processingKey}'.`, 'error');
            } finally {
                setProcessingId(null);
            }
        };
    }, []); 



    const handleDeleteAllCommissions = useCallback(() => createUtilityAction(deleteAllCommissionsCallable, 'delete-commissions', {
        title: '¿BORRAR TODAS LAS COMISIONES?',
        html: '<strong>¡ACCIÓN EXTREMADAMENTE PELIGROSA!</strong> Esto eliminará permanentemente TODO el historial de comisiones de TODOS los usuarios. Úsalo solo como el Paso 1 de la reparación.',
        icon: 'error',
        showCancelButton: true,
        confirmButtonText: 'Sí, borrar todo',
        confirmButtonColor: '#d33'
    })(), [createUtilityAction]);

    const handleRecalculateAllCommissions = useCallback(() => createUtilityAction(recalculateAllCommissionsCallable, 'recalc-commissions', {
        title: 'Reconstruir Historial de Comisiones',
        html: '<strong>PASO 2:</strong> Esto analizará todos los acuerdos y pagará las comisiones según la nueva lógica (acreditando a acuerdos con acelerador). Ejecútalo después de borrar el historial.',
        icon: 'info',
        showCancelButton: true,
        confirmButtonText: 'Sí, reconstruir comisiones'
    })(), [createUtilityAction]);

    const handleResyncBalances = useCallback((data = {}) => createUtilityAction(resyncAllBalancesCallable, 'resync-balances', {
        title: 'Resincronizar Balances Final',
        html: '<strong>PASO 3:</strong> Recalculará el balance final de TODOS los usuarios basado en el historial ya corregido y reconstruido.',
        icon: 'success',
        showCancelButton: true,
        confirmButtonText: 'Sí, resincronizar'
    })(data), [createUtilityAction]);

const handleRecalculateAllAgreements = useCallback(() => createUtilityAction(recalculateAllAgreementsProgressCallable, 'recalc-agreements', {
        title: 'Sincronizar Progreso de Acuerdos',
        html: "Esta herramienta recalculará el progreso de <strong>TODOS</strong> los acuerdos activos. Comparará su valor actual con su historial de ganancias y aplicará los ajustes necesarios al balance de los usuarios.<br/><br/><strong>Úsala si las ganancias diarias no se están reflejando en los balances.</strong>",
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Sí, Sincronizar Progreso'
    })(), [createUtilityAction]);

    const handleCorrectInvestmentTxs = useCallback(() => createUtilityAction(correctInvestmentTransactionsCallable, 'correct-invest-txs', {
        title: 'Corregir Transacciones de Inversión',
        html: '<strong>Paso 1 de 2.</strong> Esta herramienta buscará en todo el historial transacciones de "Inversión Manual" que se guardaron con un monto incorrecto y las corregirá a cero. Es el primer paso para arreglar los balances.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Sí, corregir historial'
    })(), [createUtilityAction]);

    const handleAuditAndCorrectAgreements = useCallback((data = {}) => createUtilityAction(
        auditAndCorrectAgreementsCallable,
        'audit-correct-agreements',
        {
            title: 'Auditar y Corregir Acuerdos Excedidos',
            html: "<strong>¡Acción de corrección de datos!</strong><br/>Esta herramienta buscará acuerdos que hayan superado su tope del 200%, corregirá su valor y estado. El excedente se intentará redistribuir o se deducirá del balance del usuario.",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Sí, auditar y corregir',
            confirmButtonColor: '#8b5cf6'
        }
    )(data), [createUtilityAction]);

    const handleRecalculateAllRanks = useCallback(() => createUtilityAction(
        recalculateAllRanksCallable, 
        'recalc-ranks', 
        {
            title: 'Recalcular Puntos y Rangos',
            html: "Esta herramienta borrará los puntos actuales de todos los usuarios y los <strong>recalculará desde cero</strong> basándose únicamente en acuerdos reales (no promocionales).",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Sí, recalcular todo'
        }
    )(), [createUtilityAction]);

    const handleFixAgreementIntegrity = useCallback(() => createUtilityAction(
        fixAgreementIntegrityCallable, 
        'fix-integrity', 
        {
            title: 'Sincronizar Integridad de Acuerdos',
            html: "Esta herramienta buscará <strong>acuerdos rechazados por error</strong> y eliminará <strong>acuerdos fantasma</strong> que ya no existen en la base de datos pública.",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Sí, sincronizar todo'
        }
    )(), [createUtilityAction]);

    const handleStatusUpdate = useCallback(async (agreement, newStatus, options = {}) => {
        setProcessingId(agreement.id);

        if (!agreement.privateAgreementPath) {
            console.error("Error: privateAgreementPath no encontrado en el acuerdo.", agreement);
            MySwal.fire('Error del Sistema', 'No se pudo encontrar la ruta del acuerdo privado. Contacte a soporte.', 'error');
            setProcessingId(null);
            return;
        }

        try {
            const batch = writeBatch(db);
            const publicRef = doc(db, 'public_agreements', agreement.id);
            const privateRef = doc(db, agreement.privateAgreementPath);
            const updateData = { status: newStatus, reviewedAt: serverTimestamp() };

            if (newStatus === 'aprobado') {
                updateData.isTest = options.isTest || false; // CORRECCIÓN: Se eliminó un punto y coma erróneo.
            }

            batch.update(publicRef, updateData);
            batch.update(privateRef, updateData);
            await batch.commit();
            MySwal.fire('¡Éxito!', `El acuerdo ha sido ${newStatus}.`, 'success');
        } catch (err) {
            console.error(`Error al actualizar estado a ${newStatus}:`, err);
            MySwal.fire('Error', 'No se pudo actualizar el estado.', 'error');
        } finally { 
            setProcessingId(null);
        }
    }, [createUtilityAction]);

    const runAssignGainsByDate = useCallback(async (date, dryRun = false, agreementIds = undefined, options = {}) => {
        setProcessingId('assign-gains-date');
        try {
            const payload = { date, dryRun };
            if (agreementIds && Array.isArray(agreementIds) && agreementIds.length > 0) payload.agreementIds = agreementIds;
            if (options.forceDuplicates) payload.forceDuplicates = true;
            if (options.excludeAgreementIds && options.excludeAgreementIds.length > 0) payload.excludeAgreementIds = options.excludeAgreementIds;
            const res = await assignGainsByDateCallable(payload);
            return res.data;
        } catch (err) {
            console.error('Error running assignGainsByDate:', err);
            throw err;
        } finally {
            setProcessingId(null);
        }
    }, []);

    const handleAdminProfileUpdate = useCallback(async (userId, updates, originalProfile = {}) => {
        setProcessingId(userId);
        try {
            const diffEntries = Object.entries(updates || {}).filter(([key, value]) => {
                const previous = originalProfile?.[key];
                return (value ?? '') !== (previous ?? '');
            });

            if (diffEntries.length === 0) {
                return { success: false, reason: 'no-changes' };
            }

            const diff = Object.fromEntries(diffEntries);
            const payload = { ...diff };

            if (diff.userEmail && diff.userEmail !== (originalProfile?.userEmail ?? '')) {
                const emailUpdateCallable = createApiCallable('adminUpdateUserEmail');
                await emailUpdateCallable({ targetUid: userId, newEmail: diff.userEmail });
                delete payload.userEmail;
            }

            if (Object.keys(payload).length > 0) {
                await updateDoc(doc(db, `users/${userId}/profile/data`), payload);
            }

            await addDoc(collection(doc(db, 'users', userId, 'profile', 'data'), 'admin_audit'), {
                type: 'profile_update',
                adminId: currentUser?.uid || null,
                adminEmail: currentUser?.email || null,
                changes: diff,
                previous: {
                    fullName: originalProfile?.fullName ?? null,
                    userEmail: originalProfile?.userEmail ?? null,
                    walletAddress: originalProfile?.walletAddress ?? null,
                },
                createdAt: serverTimestamp(),
            });

            return { success: true };
        } catch (err) {
            console.error("Error al actualizar perfil:", err);
            return { success: false, error: err };
        } finally {
            setProcessingId(null);
        }
    }, [currentUser]);

    const handleAdminChangePassword = useCallback(async (userId, userEmail) => {
        const { value:newPassword } = await MySwal.fire({
            title: `Establecer nueva contraseña para ${userEmail}`,
            input: 'password',
            inputLabel: 'Nueva Contraseña Temporal',
            inputPlaceholder: 'Introduce la nueva contraseña...',
            showCancelButton: true,
            confirmButtonText: 'Establecer Contraseña',
            inputValidator: (v) => !v || v.length < 6 ? 'La contraseña debe tener al menos 6 caracteres.' : null
        });

        if (newPassword) {
            setProcessingId(userId);
            try {
                await createApiCallable('adminResetUserPassword')({ targetUid: userId, newPassword });
                MySwal.fire('¡Éxito!', 'La contraseña ha sido cambiada. Comunícasela al usuario.', 'success');
            } catch (err) {
                MySwal.fire('Error', `No se pudo cambiar la contraseña: ${err.message}`, 'error');
            } finally {
                setProcessingId(null);
            }
        }
    }, []);

   // REEMPLAZA ESTA FUNCIÓN EN TU src/context/AdminContext.js

const handleAdminAdjustBalance = useCallback(async (targetUid, amount, reason, agreementId = null) => { // agreementId ahora tiene un valor por defecto
    setProcessingId(targetUid);
    try {
        const result = await adminAdjustUserBalanceCallable({ targetUid, amount, reason, agreementId });

        await addDoc(collection(doc(db, 'users', targetUid, 'profile', 'data'), 'admin_audit'), {
            type: 'balance_adjustment',
            adminId: currentUser?.uid || null,
            adminEmail: currentUser?.email || null,
            amount,
            reason,
            agreementId: agreementId || null,
            createdAt: serverTimestamp(),
        });

        return { success: true, message: result.data?.message };
    } catch (err) {
        console.error("Error al ajustar saldo:", err);
        return { success: false, error: err };
    } finally {
        setProcessingId(null);
    }
}, [currentUser]);

    const handleDeleteUser = useCallback(async (userId, userEmail) => {
        const result = await MySwal.fire({
            title: '¿Estás seguro?',
            html: `Estás a punto de <strong>desactivar</strong> al usuario <strong>${userEmail}</strong>. No podrá iniciar sesión y sus referidos serán reasignados. Esta acción es reversible por un administrador.`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Sí, desactivar',
            cancelButtonText: 'Cancelar',
            confirmButtonColor: '#dc3545',
        });

        if (!result?.isConfirmed) {
            return;
        }

        setProcessingId(userId);
        try {
          const res = await deleteUserCallable({ targetUid: userId });
            await addDoc(collection(doc(db, 'users', userId, 'profile', 'data'), 'admin_audit'), {
                type: 'user_deactivation',
                description: `Desactivación del usuario ${userEmail}.`,
                adminId: currentUser?.uid || null,
                adminEmail: currentUser?.email || null,
                createdAt: serverTimestamp(),
            });
            MySwal.fire('¡Desactivado!', res.data.message, 'success');
        } catch (err) {
            console.error("Error al desactivar usuario:", err);
            MySwal.fire('Error', err.message || 'No se pudo desactivar al usuario.', 'error');
        } finally {
            setProcessingId(null);
        }
    }, [currentUser]);

    const handleReactivateUser = useCallback(async (userId, userEmail) => {
        const result = await MySwal.fire({
            title: 'Reactivar usuario',
            html: `Vas a reactivar al usuario <strong>${userEmail}</strong>. Recuperará el acceso a la plataforma de inmediato.`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'Sí, reactivar',
            cancelButtonText: 'Cancelar',
            confirmButtonColor: '#0f766e',
        });

        if (!result?.isConfirmed) {
            return;
        }

        setProcessingId(userId);
        try {
            const res = await reactivateUserCallable({ targetUid: userId });
            await addDoc(collection(doc(db, 'users', userId, 'profile', 'data'), 'admin_audit'), {
                type: 'user_reactivation',
                description: `Reactivación del usuario ${userEmail}.`,
                adminId: currentUser?.uid || null,
                adminEmail: currentUser?.email || null,
                createdAt: serverTimestamp(),
            });
            MySwal.fire('¡Reactivado!', res.data.message, 'success');
        } catch (err) {
            console.error('Error al reactivar usuario:', err);
            MySwal.fire('Error', err.message || 'No se pudo reactivar al usuario.', 'error');
        } finally {
            setProcessingId(null);
        }
    }, [currentUser]);

    // En AdminContext.jsx, junto a los otros useCallback
const handleCorrectNegativeBalances = useCallback(( ) => createUtilityAction(correctNegativeBalancesCallable, 'correct-negatives', {
    title: 'Corregir Balances Negativos',
    html: 'Esta herramienta buscará a <strong>todos los usuarios con saldo disponible para retiro negativo</strong> y lo establecerá en <strong>cero</strong>.<br/><br/>Se creará una transacción de ajuste en su historial para auditar la corrección. ¿Estás seguro?',
    icon: 'warning',
    showCancelButton: true,
    confirmButtonText: 'Sí, corregir a cero',
    confirmButtonColor: '#f59e0b',
    cancelButtonText: 'Cancelar'
})(), [createUtilityAction]);

    // CORRECCIÓN: Se refactoriza para usar createUtilityAction y ser más robusta.
    const handleVerifyAndPayMissingCommissions = useCallback(async () => { 
        const { value: formValues } = await MySwal.fire({
            title: 'Verificar y Pagar Comisiones Faltantes',
            html: `
                <p>Esta herramienta buscará acuerdos aprobados dentro de un rango de fechas y pagará las comisiones de referido que no se hayan generado.</p>
                <p>Activa la casilla si necesitas incluir acuerdos promocionales que normalmente no pagan comisiones.</p>
                <input type="date" id="swal-start-date" class="swal2-input">
                <input type="date" id="swal-end-date" class="swal2-input">
                <label style="display:flex;align-items:center;gap:8px;margin-top:12px;">
                    <input type="checkbox" id="swal-include-promo">
                    <span>Incluir acuerdos de promoción sin permiso explícito</span>
                </label>
            `,
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'Verificar Ahora',
            focusConfirm: false,
            preConfirm: () => {
                const startDate = document.getElementById('swal-start-date').value;
                const endDate = document.getElementById('swal-end-date').value;
                const includePromoAgreements = document.getElementById('swal-include-promo').checked;
                if (!startDate || !endDate) {
                    Swal.showValidationMessage('Debes seleccionar una fecha de inicio y una de fin.');
                }
                return { startDate, endDate, includePromoAgreements };
            }
        });
    
        if (formValues) {
            const promoNote = formValues.includePromoAgreements
                ? '<br/><strong>Se incluirán acuerdos promocionales aunque no tengan permiso individual.</strong>'
                : '';

            createUtilityAction(
                verifyAndPayMissingCommissionsCallable, 
                'verify-commissions', 
                { title: 'Confirmar Verificación', html: `Se buscarán comisiones faltantes entre <strong>${formValues.startDate}</strong> y <strong>${formValues.endDate}</strong>. ¿Continuar?${promoNote}`, icon: 'info', showCancelButton: true, confirmButtonText: 'Sí, verificar' }
            )({ startDate: formValues.startDate, endDate: formValues.endDate, includePromoAgreements: formValues.includePromoAgreements });
        }
    }, [createUtilityAction]);


    const handleCancelAgreement = useCallback(async (agreement) => {
        const { value: reason } = await MySwal.fire({
            title: 'Cancelar Acuerdo',
            html: `Estás a punto de cancelar el acuerdo <strong>#${agreement.id.substring(0, 6)}</strong> del usuario <strong>${agreement.userEmail}</strong>. Esta acción es irreversible.`,
            input: 'text',
            inputLabel: 'Motivo de la cancelación',
            inputPlaceholder: 'Escribe un motivo claro para el usuario...',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#dc3545',
            confirmButtonText: 'Sí, cancelar acuerdo',
            cancelButtonText: 'No, mantener',
            inputValidator: (value) => {
                if (!value || value.trim().length < 10) {
                    return 'Debes introducir un motivo de al menos 10 caracteres.';
                }
            }
        });

        if (reason) {
            setProcessingId(agreement.id);
            try {
                await cancelAgreementCallable({ agreementId: agreement.id, reason: reason.trim() });
                await addDoc(collection(doc(db, 'users', agreement.userId, 'profile', 'data'), 'admin_audit'), {
                    type: 'agreement_cancellation',
                    description: `Cancelación del acuerdo #${agreement.id.substring(0, 6)}.`,
                    adminId: currentUser?.uid || null,
                    adminEmail: currentUser?.email || null,
                    details: { agreementId: agreement.id, reason: reason.trim() },
                    createdAt: serverTimestamp(),
                });
                MySwal.fire('¡Cancelado!', 'El acuerdo ha sido cancelado exitosamente.', 'success');
            } catch (error) {
                console.error("Error al cancelar el acuerdo:", error);
                MySwal.fire('Error', `No se pudo cancelar el acuerdo: ${error.message}`, 'error');
            } finally {
                setProcessingId(null);
            }
        }
    }, [currentUser?.uid, currentUser?.email]);

    // --- NUEVO: Desactivar acelerador de un acuerdo ---
    const handleDeactivateAccelerator = useCallback(async (agreement) => {
        const { value: reason } = await MySwal.fire({
            title: 'Desactivar Acelerador',
            html: `Estás a punto de desactivar el acelerador para el acuerdo <strong>#${agreement.id.substring(0, 6)}</strong>.`,
            input: 'text',
            inputLabel: 'Motivo de la desactivación',
            inputPlaceholder: 'Escribe un motivo claro...',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#ffc107',
            confirmButtonText: 'Sí, desactivar',
            cancelButtonText: 'Cancelar',
            inputValidator: (value) => {
                if (!value || value.trim().length < 10) {
                    return 'Debes introducir un motivo de al menos 10 caracteres.';
                }
            }
        });

        if (reason) {
            setProcessingId(agreement.id);
            try {
                await deactivateAcceleratorCallable({ agreementId: agreement.id, reason: reason.trim() });
                await addDoc(collection(doc(db, 'users', agreement.userId, 'profile', 'data'), 'admin_audit'), {
                    type: 'accelerator_deactivation',
                    description: `Desactivación del acelerador para el acuerdo #${agreement.id.substring(0, 6)}.`,
                    adminId: currentUser?.uid || null,
                    adminEmail: currentUser?.email || null,
                    details: { agreementId: agreement.id, reason: reason.trim() },
                    createdAt: serverTimestamp(),
                });
                MySwal.fire('¡Desactivado!', 'El acelerador ha sido desactivado exitosamente.', 'success');
            } catch (error) {
                console.error("Error al desactivar el acelerador:", error);
                MySwal.fire('Error', `No se pudo desactivar el acelerador: ${error.message}`, 'error');
            } finally {
                setProcessingId(null);
            }
        }
    }, [currentUser?.uid, currentUser?.email]);

    const handleApproveAcceleratorRequest = useCallback(async (request) => {
        setProcessingId(request.agreementId);
        try {
            await approveAcceleratorRequestCallable({ requestId: request.id, requestType: request.type, agreementId: request.agreementId });
            MySwal.fire('¡Aprobado!', 'El acelerador ha sido activado para este acuerdo.', 'success');
        } catch (error) {
            console.error("Error al aprobar la solicitud del acelerador:", error);
            MySwal.fire('Error', `No se pudo aprobar la solicitud: ${error.message}`, 'error');
        } finally {
            setProcessingId(null);
        }
    }, []);

    // --- NUEVO: Reactivar acelerador de un acuerdo ---
    const handleReactivateAccelerator = useCallback(async (agreement) => {
        setProcessingId(agreement.id);
        try {
            await manualReactivateAcceleratorCallable({ agreementId: agreement.id });
            MySwal.fire('¡Reactivado!', 'El acelerador ha sido reactivado exitosamente.', 'success');
        } catch (error) {
            console.error("Error al reactivar el acelerador:", error);
            MySwal.fire('Error', `No se pudo reactivar el acelerador: ${error.message}`, 'error');
        } finally {
            setProcessingId(null);
        }
    }, []);

    const handlePermissionsChange = useCallback(async (userId, payload) => {
        setProcessingId(userId);

        const role = typeof payload === 'string' ? payload : payload?.role;
        let permissions = (payload && typeof payload === 'object' && payload.permissions !== undefined)
            ? payload.permissions
            : undefined;

        if (permissions === undefined && role) {
            const preset = rolePermissionPresets[role];
            if (preset) {
                permissions = { ...preset };
            }
        }

        try {
            if (role) {
                const setUserRoleCallable = createApiCallable('setUserRole');
                await setUserRoleCallable({ targetUid: userId, role });
            }

            if (permissions !== undefined) {
                await updateDoc(doc(db, `users/${userId}/profile/data`), { permissions });
            }

            await addDoc(collection(doc(db, 'users', userId, 'profile', 'data'), 'admin_audit'), {
                type: 'permissions_change',
                description: `Cambio de rol/permisos para el usuario.`,
                adminId: currentUser?.uid || null,
                adminEmail: currentUser?.email || null,
                details: { role, permissions: permissions || 'No especificado' },
                createdAt: serverTimestamp(),
            });
            MySwal.fire('¡Éxito!', 'Los permisos del usuario han sido actualizados.', 'success');
        } catch (err) {
            console.error("Error al cambiar permisos:", err);
            MySwal.fire('Error', err.message || 'No se pudieron cambiar los permisos.', 'error');
        } finally {
            setProcessingId(null);
        }
    }, [rolePermissionPresets, currentUser]);

    const handleToggleWithdrawals = useCallback(async (enabled) => {
        setProcessingId('platform_settings');
        try {
            // Llama a la Cloud Function del backend en lugar de actualizar Firestore directamente
            await togglePlatformSettingCallable({ settingKey: 'withdrawalsEnabled', newValue: enabled });
            // El listener de 'platform_config/main' en AuthContext se encargará de actualizar el estado.
            // No es necesario un Swal.fire aquí, ya que el cambio en el botón será la confirmación visual.
        } catch (err) {
            console.error("Error al cambiar el estado de los retiros:", err);
            MySwal.fire('Error', 'No se pudo actualizar la configuración.', 'error');
        } finally {
            setProcessingId(null);
        }
    }, []); // No se necesitan dependencias porque togglePlatformSettingCallable es estable

    const handleProcessWithdrawal = useCallback(async (request, newStatus, details) => {
        setProcessingId(request.id);
        try {
            await processWithdrawalCallable({ requestId: request.id, newStatus, details });
            MySwal.fire('¡Éxito!', `El retiro ha sido marcado como ${newStatus}.`, 'success');
        } catch(err) {
            MySwal.fire('Error', `No se pudo procesar el retiro: ${err.message}`, 'error');
        } finally {
            setProcessingId(null);
        }
    }, []);

    const handleAssignGains = useCallback(async (agreementIds, percentage, options = {}) => {
        setProcessingId('assign-gains');
        try {
            const payload = { agreementIds, percentage };
            if (options.dryRun) payload.dryRun = true;
            if (options.forceDuplicates) payload.forceDuplicates = true;
            if (options.excludeAgreementIds && options.excludeAgreementIds.length > 0) payload.excludeAgreementIds = options.excludeAgreementIds;
            const result = await assignDailyGainsCallable(payload);
            if (options.dryRun) return result.data;
            MySwal.fire('¡Éxito!', result.data.message, 'success');
            return result.data;
        } catch (err) {
            console.error('Error al asignar ganancias:', err);
            if (!options.dryRun) MySwal.fire('Error', err.message || 'No se pudo asignar las ganancias.', 'error');
            throw err;
        } finally {
            setProcessingId(null);
        }
    }, []);

    // CORRECCIÓN: La función estaba incompleta. Se ha corregido para que muestre un diálogo de confirmación.
    const handleRevertPayment = useCallback(async (agreementIds) => {
        const confirmResult = await MySwal.fire({
            title: 'Revertir Último Pago',
            html: `Se revertirá el último pago de ROI para <strong>${agreementIds.length}</strong> acuerdo(s) seleccionado(s).<br/><br/><strong>¡CUIDADO!</strong> Esta acción es irreversible.`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Sí, revertir pagos',
            confirmButtonColor: '#dc3545',
            cancelButtonText: 'Cancelar'
        });

        if (confirmResult.isConfirmed) {
            setProcessingId('revert-payment');
            try {
                const result = await revertLastPaymentCallable({ agreementIds });
                MySwal.fire('¡Éxito!', result.data.message, 'success');
            } catch (err) {
                console.error('Error al revertir pago:', err);
                MySwal.fire('Error', err.message || 'No se pudo revertir el pago.', 'error');
            } finally {
                setProcessingId(null);
            }
        }
    }, []);

    // --- NUEVO: Pagar ganancias por fecha específica ---
    const handleAssignGainsByDate = useCallback(async () => {
        const { value: formValues } = await MySwal.fire({
            title: 'Pagar Resultados por Fecha',
            html: `<p>Esta herramienta pagará el ROI para una fecha específica a <strong>TODOS</strong> los acuerdos activos (normales y acelerados), aplicando la tasa correcta a cada uno.</p><p>Es segura y no duplicará pagos.</p><input type="date" id="swal-date" class="swal2-input">`,
            icon: 'info',
            showCancelButton: true,
            confirmButtonText: 'Pagar Ahora',
            focusConfirm: false,
            preConfirm: () => {
                const date = document.getElementById('swal-date').value;
                if (!date) {
                    Swal.showValidationMessage('Debes seleccionar una fecha.');
                }
                return { date };
            }
        });

        if (formValues) {
            createUtilityAction(assignGainsByDateCallable, 'assign-gains-by-date', {
                title: 'Confirmar Pago', html: `Se pagará el rendimiento correspondiente a <strong>TODOS</strong> los acuerdos para la fecha <strong>${formValues.date}</strong>. ¿Continuar?`, icon: 'question', showCancelButton: true, confirmButtonText: 'Sí, pagar'
            })(formValues);
        }
    }, []);

    const handleRevertGainsByDate = useCallback(async () => {
        const { value: dateString } = await MySwal.fire({
            title: 'Revertir Resultados por Fecha',
            html: 'Ingresa la fecha (en formato <strong>AAAA-MM-DD</strong>) de las ganancias que deseas revertir. Esto eliminará todas las transacciones de ROI de ese día y ajustará los saldos.<br/><br/><strong>¡CUIDADO!</strong> Esta acción es irreversible.',
            input: 'text',
            inputPlaceholder: 'Ej: 2024-05-21',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Sí, revertir por fecha',
            confirmButtonColor: '#dc3545',
            cancelButtonText: 'Cancelar',
            inputValidator: (value) => {
                if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) {
                    return 'Debes ingresar una fecha en el formato AAAA-MM-DD.';
                }
            }
        });

        if (dateString) {
            createUtilityAction(revertGainsByDateCallable, 'revert-gains-by-date', { title: 'Confirmar Reversión', html: `Se revertirán <strong>TODAS</strong> las ganancias de ROI pagadas en la fecha <strong>${dateString}</strong>. ¿Continuar?`, icon: 'warning', showCancelButton: true, confirmButtonText: 'Confirmar' })({ dateString });
        }
    }, [createUtilityAction]);
    
    const handleCleanReversalHistory = useCallback(() => createUtilityAction(
        cleanReversalHistoryCallable, 
        'clean-reversal-history', 
        {
            title: 'Limpiar Historial de Reversiones',
            html: "Esta acción buscará y <strong>eliminará permanentemente</strong> todos los registros de 'reversion_ganancia' del historial de los usuarios. Úsala para limpiar la vista del usuario después de una corrección. <strong>No afectará los saldos.</strong>",
            icon: 'info',
            showCancelButton: true,
            confirmButtonText: 'Sí, limpiar historial'
        }
    )(), [createUtilityAction]);

    const handleRevertBalanceAdjustment = useCallback(async (targetUid, amount, originalReason) => {
        setProcessingId(targetUid);
        try {
            const revertAmount = -Math.abs(amount);
            const reason = `Reversión de ajuste: ${originalReason}`;
            const result = await adminAdjustUserBalanceCallable({ targetUid, amount: revertAmount, reason });
            MySwal.fire('¡Revertido!', result.data.message, 'success');
        } catch (err) {
            console.error('Error al revertir ajuste:', err);
            MySwal.fire('Error', err.message || 'No se pudo revertir el ajuste.', 'error');
        } finally {
            setProcessingId(null);
        }
    }, []);

    

    const handleRecalculateMissingCommissions = useCallback(async () => {
        const confirmResult = await MySwal.fire({
            title: 'Recalcular Comisiones Faltantes',
            text: 'Este proceso analizará todos los acuerdos históricos y pagará las comisiones de referidos que no se hayan pagado por errores pasados. Es seguro ejecutarlo varias veces.',
            icon: 'info',
            showCancelButton: true,
            confirmButtonText: 'Sí, recalcular',
            cancelButtonText: 'Cancelar',
        });

        if (!confirmResult.isConfirmed) return;

        setProcessingId('recalculate-commissions');
        try {
            const result = await recalculateMissingCommissionsCallable();
            MySwal.fire('¡Éxito!', result.data.message, 'success');
        } catch (err) {
            MySwal.fire('Error', err.message || 'No se pudo completar el recálculo.', 'error');
        } finally {
            setProcessingId(null);
        }
    }, []);

    // const handleMigrateOldAgreements = useCallback(() => createUtilityAction(migrateOldAgreementsCallable, 'migrate-agreements', { title: '¿Migrar Acuerdos Antiguos?', text: 'Esto actualizará la estructura de acuerdos antiguos. Es seguro ejecutarlo varias veces.', icon: 'info', showCancelButton: true, confirmButtonText: 'Sí, migrar' })(), [createUtilityAction]);
    // const handleRecalculateData = useCallback(() => createUtilityAction(recalculateDataCallable, 'recalculate-data', { title: '¿Sincronizar Datos Históricos?', text: 'Esto recalculará contadores de equipo y comisiones faltantes. Es seguro ejecutarlo varias veces.', icon: 'info', showCancelButton: true, confirmButtonText: 'Sí, sincronizar' })(), [createUtilityAction]);
    const handleReprocessStuckAgreements = useCallback(() => createUtilityAction(reprocessStuckAgreementsCallable, 'reprocess-agreements', { title: '¿Reparar Acuerdos Atascados?', text: 'Esto buscará acuerdos aprobados que no se procesaron correctamente y los reparará. Es seguro ejecutarlo.', icon: 'warning', showCancelButton: true, confirmButtonText: 'Sí, reparar' })(), [createUtilityAction]);

    // const handleRevertConsolidatedGains = useCallback(() => createUtilityAction(revertConsolidatedGainsCallable, 'revert-gains', {
    //     title: '¿Revertir Ganancias Consolidadas?',
    //     html: '<strong>¡ACCIÓN MUY PELIGROSA!</strong><br/>Esto buscará y eliminará TODAS las transacciones de "Ganancias históricas consolidadas" y restará el monto del balance de los usuarios afectados. Úsalo solo una vez para corregir el error inicial.',
    //     icon: 'error',
    //     showCancelButton: true,
    //     confirmButtonText: 'Sí, revertir ahora',
    //     confirmButtonColor: '#d33',
    //     cancelButtonText: 'Cancelar'
    // })(), [createUtilityAction]);

    const handleSmallOwnerAction = useCallback(async (request, newStatus) => {
        setProcessingId(request.id);
        try {
            await processSmallOwnerRequestCallable({ requestId: request.id, newStatus });
            await addDoc(collection(doc(db, 'users', request.userId, 'profile', 'data'), 'admin_audit'), {
                type: 'small_owner_request_processed',
                description: `Procesada solicitud de Pequeño Dueño para ${request.userEmail} como '${newStatus}'.`,
                adminId: currentUser?.uid || null,
                adminEmail: currentUser?.email || null,
                details: { requestId: request.id, newStatus },
                createdAt: serverTimestamp(),
            });
            MySwal.fire('¡Éxito!', `La solicitud ha sido marcada como ${newStatus}.`, 'success');
        } catch(err) {
            MySwal.fire('Error', `No se pudo procesar la solicitud: ${err.message}`, 'error');
        } finally {
            setProcessingId(null);
        }
    }, [currentUser]);

// En AdminContext.jsx, junto a las otras funciones useCallback

    const handleCorrectAllAgreements = useCallback(async () => {
        const confirmResult = await MySwal.fire({
            title: '¿Ejecutar Corrección Total?',
            html: `
                <p><strong>¡ATENCIÓN!</strong> Esta es una herramienta de corrección potente. Realizará la siguiente acción:</p>
                <ol style="text-align: left; margin-left: 20px;">
                    <li><strong>Resincronizará el balance de TODOS los usuarios</strong> basándose en su historial completo de transacciones.</li>
                </ol>
                <p>Las demás tareas como completar acuerdos se manejan automáticamente por el sistema.</p>
                <p>Úsala después de una reversión o si los balances están incorrectos.</p>
            `,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Sí, corregir todo',
            cancelButtonText: 'Cancelar',
            confirmButtonColor: '#f59e0b',
        });

        if (!confirmResult.isConfirmed) return;

        setProcessingId('correct-all');
        try {
            // Esta herramienta ahora solo llama a la resincronización de balances, que es la acción principal y segura.
            const result = await resyncAllBalancesCallable(); // CORRECCIÓN: Llamar a la función correcta que sí existe.
            MySwal.fire('¡Éxito!', result.data.message, 'success');
        } catch (err) {
            MySwal.fire('Error', err.message || 'No se pudo completar la corrección.', 'error');
        } finally {
            setProcessingId(null);
        }
    }, [createUtilityAction]);


const handleAdminCreateAgreement = useCallback(async (targetUid, amount, activateSmallOwner, activateAccelerator, notes, isTest = false, allowTestCommissions = false) => {
    setProcessingId(`create-agreement-${targetUid}`);
    console.log("AdminContext: handleAdminCreateAgreement llamado con targetUid:", targetUid, "isTest:", isTest, "allowTestCommissions:", allowTestCommissions);
    try {
        // Y lo enviamos al backend
        const res = await adminCreateManualAgreementCallable({ targetUid, amount, activateSmallOwner, activateAccelerator, notes, isTest, allowTestCommissions });

        // --- AÑADIDO: Registrar la acción en la auditoría desde el cliente ---
        // Esto asume que el backend devuelve el ID del acuerdo en `res.data.agreementId`
        const agreementId = res.data?.agreementId;
        await addDoc(collection(doc(db, 'users', targetUid, 'profile', 'data'), 'admin_audit'), {
            type: 'manual_agreement_creation',
            description: `Creación de acuerdo manual por $${amount}.`,
            adminId: currentUser?.uid || null,
            adminEmail: currentUser?.email || null,
            amount,
            notes,
            activateAccelerator,
            isTest,
            agreementId: agreementId || null,
            createdAt: serverTimestamp(),
        });

        MySwal.fire('¡Acuerdo Creado!', res.data.message, 'success');
    } catch (error) {
        MySwal.fire('Error', error.message, 'error');
    } finally {
        setProcessingId(null);
    }
}, [currentUser]);

    const handleBootstrapAdmin = useCallback(async () => {
        const { value: formValues } = await MySwal.fire({
            title: 'Asignar Administrador de Emergencia',
            html:
                '<p>Esta es una operación de alto riesgo. Necesitarás el <b>UID del usuario</b> y la <b>clave secreta</b>.</p>' +
                '<input id="swal-input1" class="swal2-input" placeholder="UID del Usuario">' +
                '<input id="swal-input2" class="swal2-input" placeholder="Clave Secreta">',
            focusConfirm: false,
            showCancelButton: true,
            confirmButtonText: 'Asignar Admin',
            preConfirm: () => {
                const targetUid = document.getElementById('swal-input1').value;
                const secretKey = document.getElementById('swal-input2').value;
                if (!targetUid || !secretKey) {
                    Swal.showValidationMessage(`Por favor, completa ambos campos`);
                }
                return { targetUid, secretKey };
            }
        });

        if (formValues) {
            setProcessingId('bootstrap-admin');
            try {
                const result = await bootstrapAdminCallable(formValues);
                MySwal.fire('¡Éxito!', result.data.message, 'success');
            } catch (err) {
                MySwal.fire('Error', err.message || 'No se pudo asignar el administrador.', 'error');
            } finally {
                setProcessingId(null);
            }
        }
    }, []);

    // --- FUNCIONES DE CARGA BAJO DEMANDA PARA MEJOR RENDIMIENTO ---
    const loadUsers = useCallback(async (limitCount = 100) => {
        if (!hasPrivilegedAccess) return;

        try {
            const profilesQuery = query(collectionGroup(db, 'profile'), limit(limitCount));
            const snapshot = await getDocs(profilesQuery);
            const userData = snapshot.docs.map(doc => ({
                id: doc.ref.parent.parent.id, // user ID
                ...doc.data()
            }));
            setUsers(userData);
        } catch (error) {
            console.error('Error loading users:', error);
            MySwal.fire('Error', 'No se pudieron cargar los usuarios.', 'error');
        }
    }, [hasPrivilegedAccess]);

    const loadMoreFinancialLog = useCallback(async (additionalLimit = 100) => {
        if (!hasPrivilegedAccess) return;

        try {
            const currentCount = financialLog.length;
            const moreQuery = query(
                collection(db, 'financial_log'),
                orderBy('createdAt', 'desc'),
                limit(currentCount + additionalLimit)
            );
            const snapshot = await getDocs(moreQuery);
            const logData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setFinancialLog(logData);
        } catch (error) {
            console.error('Error loading more financial log:', error);
            MySwal.fire('Error', 'No se pudieron cargar más registros financieros.', 'error');
        }
    }, [hasPrivilegedAccess, financialLog.length]);

    // const handleRecalculateAllCommissions = useCallback(() => createUtilityAction(recalculateAllCommissionsCallable, 'recalc-all-commissions', {
    //     title: '¿Reconstruir Historial de Comisiones?',
    //     html: "<strong>¡Acción Potente!</strong> Esto recalculará <strong>TODAS</strong> las comisiones de referidos desde cero basándose en los acuerdos existentes. Úsala solo si sospechas que hay errores fundamentales en las comisiones ya pagadas.",
    //     icon: 'warning',
    //     showCancelButton: true,
    //     confirmButtonText: 'Sí, reconstruir todo',
    //     confirmButtonColor: '#dc3545',
    //     cancelButtonText: 'Cancelar'
    // })(), [createUtilityAction]);

    // const handleDeleteAllCommissions = useCallback(() => createUtilityAction(deleteAllCommissionsCallable, 'delete-all-commissions', {
    //     title: '¿BORRAR TODAS LAS COMISIONES?',
    //     html: "<strong>¡ACCIÓN EXTREMADAMENTE PELIGROSA!</strong> Esto eliminará de forma permanente <strong>TODAS</strong> las transacciones de comisión de la base de datos. Úsala solo como último recurso si la reconstrucción no funciona. <br/><br/><strong>Después de ejecutar esto, DEBES usar la herramienta 'Reconstruir Historial de Comisiones'.</strong>",
    //     icon: 'error',
    //     showCancelButton: true,
    //     confirmButtonText: 'Sí, entiendo, borrar todo',
    //     confirmButtonColor: '#d33',
    //     cancelButtonText: 'Cancelar'
    // })(), [createUtilityAction]);

    const handleCleanAndRebuildCommissions = useCallback(() => createUtilityAction(cleanAndRebuildAllCommissionsCallable, 'clean-rebuild-commissions', {
        title: '¿Limpiar y Reconstruir Comisiones?',
        html: "<strong>¡Acción Potente y Definitiva!</strong> Este proceso primero <strong>BORRARÁ</strong> todas las comisiones existentes y luego las <strong>RECONSTRUIRÁ</strong> desde cero. Es la herramienta correcta para solucionar duplicados o errores en el historial de comisiones.",
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Sí, Limpiar y Reconstruir',
        confirmButtonColor: '#dc3545',
        cancelButtonText: 'Cancelar'
    })(), [createUtilityAction]);

    // --- NUEVO: Acción para sincronizar el tamaño de los equipos ---
    const handleResyncTeamSizes = useCallback(() => createUtilityAction(
        resyncTeamSizesCallable, 
        'resync-team-sizes', 
        { title: '¿Sincronizar Contadores de Equipo?', html: "Esta herramienta recalculará el <strong>tamaño del equipo</strong> de todos los usuarios para corregir posibles inconsistencias. Es seguro ejecutarla en cualquier momento.", icon: 'info', showCancelButton: true, confirmButtonText: 'Sí, sincronizar' }
    )(), [createUtilityAction]);


    const value = {
        currentUser, // Add currentUser to the context value
        userProfile,
        users,
        agreements: allAgreements, // backward-compat alias for components expecting `agreements`
        allAgreements, // CORRECCIÓN: Usar el nombre de estado correcto
        financialLog, // <-- EXPONER EL NUEVO ESTADO
        smallOwnerRequests,
        agreementsSummary,
        settings: platformConfig,
        loading,
        error,
        processingId,
        handleStatusUpdate,
        handleAdminProfileUpdate,
        handleAdminChangePassword,
        handleAdminAdjustBalance,
        handleDeleteUser,
        handleReactivateUser,
        handlePermissionsChange,
        handleToggleWithdrawals,
        handleCancelAgreement,
        handleDeactivateAccelerator, // <-- AÑADIDO
        handleApproveAcceleratorRequest, // <-- AÑADIDO
        handleReactivateAccelerator, // <-- AÑADIDO
        handleProcessWithdrawal,
        handleSmallOwnerAction,
        // handleMigrateOldAgreements, // Comentado
        // handleRecalculateData, // Comentado
        handleBootstrapAdmin,
        handleReprocessStuckAgreements,
        handleAssignGains,
        handleRevertBalanceAdjustment,
        handleRevertPayment,
        handleAssignGainsByDate, // <-- NUEVO
        handleRevertGainsByDate, // <-- NUEVO
        handleCleanReversalHistory, // <-- NUEVO
        handleResyncBalances,
        handleRecalculateMissingCommissions, 
        handleCorrectAllAgreements, // Comentado pero la función interna es útil
        handleCorrectNegativeBalances,
        handleVerifyAndPayMissingCommissions,
        handleCleanAndRebuildCommissions,
        handleAdminCreateAgreement,
        handleCorrectInvestmentTxs, 
        handleRecalculateAllRanks,
        handleFixAgreementIntegrity,
        handleRecalculateAllAgreements, 
        handleAuditAndCorrectAgreements,
        handleDeleteAllCommissions,
        handleRecalculateAllCommissions,
        handleResyncTeamSizes, // <-- NUEVO
        getFinancialLog: getFinancialLogCallable, // <-- NUEVO: Exponer la función
        runAssignGainsByDate, // Ejecutar pago por fecha (callable)
        adminUnreadCount,
        // --- FUNCIONES DE CARGA BAJO DEMANDA ---
        loadUsers,
        loadMoreFinancialLog,
    };
    
    return (
        <AdminContext.Provider value={value}>
            {children}
        </AdminContext.Provider>
    );
};